import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {enterAnimation, listStagger} from '@app/support/animations/fade.animation';
import {Observable, Subject} from 'rxjs';
import {User} from '@app/core/models/user.model';
import {AuthService} from '@app/core/services/auth.service';
import {BulletinBoardPost} from '@app/core/models/bulletinBoardPost.model';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {HttpService} from '@app/core/services/http.service';
import {Emojify} from '@app/support/pipes/emoji.pipe';

import {DateService} from '@app/core/services/date.service';
import {
	ConfirmPopupComponent,
	EQOButtonType,
	EQOColor,
	EQOConfirmPopupInputType,
	EQOIcon,
	EQORebrandColor,
	EQORebrandIcon,
} from '@quickorder/angular-web-lib';
import {Store} from '@ngrx/store';
import {IAppState} from '@app/core/store/state/app.state';
import {
	CreateBulletinBoardComment,
	DeleteBulletinBoardComment,
	DeleteBulletinPost,
	DislikePost,
	GetBulletinPost,
	GetBulletinPostComments,
	GetPostSeenByInfo,
	LikePost,
	PatchBulletinBoardComment,
	SetSelectedPostForComments,
} from '@app/core/store/actions/bulletin-board.actions';
import {selectUser} from '@app/core/store/selectors/session.selectors';
import {Pagination} from '@app/core/models/pagination.model';
import {
	selectBulletinBoardCommentsPagination,
	selectSelectedPostIdForComments
} from '@app/core/store/selectors/bulletin-board.selectors';
import {TranslateService} from '@ngx-translate/core';
import {BulletinBoardPostSeenByInfoComponent} from '@app/modules/shared/components/bulletin-posts/components/bulletin-board-post-seen-by-info/bulletin-board-post-seen-by-info.component';
import {BulletinBoardPostLikesInfoComponent} from '@app/modules/shared/components/bulletin-posts/components/bulletin-board-post-likes-info/bulletin-board-post-likes-info.component';
import {trackById, trackByIndex} from '@app/core/helpers';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {filter, takeUntil} from 'rxjs/operators';

export interface IBulletinPostGroup {
	text: string;
	group: string;
	posts: BulletinBoardPost[];
}

@Component({
	selector: 'bulletin-posts',
	templateUrl: './bulletin-posts.component.html',
	styleUrls: ['./bulletin-posts.component.scss'],
	providers: [Emojify],
	animations: [listStagger, enterAnimation],
})
export class BulletinPostsComponent implements OnInit, OnDestroy {

	public ICON = EQOIcon;
	public COLOR = EQOColor;

	public EQORebrandIcon: typeof EQORebrandIcon = EQORebrandIcon;
	public EQORebrandColor: typeof EQORebrandColor = EQORebrandColor;

	@Input() public postGroups: IBulletinPostGroup[];
	@Input() public redirectToMessageBoard: boolean = false;
	@Input() public showButtons: boolean = true;
	@Input() public showCreateNewComment: boolean = true;
	@Output() public deleted = new EventEmitter();

	public user$: Observable<User> = this.store.select(selectUser);
	public commentsPagination$: Observable<Pagination> = this.store.select(selectBulletinBoardCommentsPagination);
	public commentsPostIdSelected$: Observable<number> = this.store.select(selectSelectedPostIdForComments);

	public trackById = trackById;
	public trackByIndex = trackByIndex;
	public commentsPagination: Pagination;

	public form: FormGroup;

	private _selectedPostId: number;

	private _destroy$: Subject<void> = new Subject<void>();

	constructor(
		public dialog: MatDialog,
		public router: Router,
		public httpService: HttpService,
		public authService: AuthService,
		public emojify: Emojify,
		private dateService: DateService,
		private store: Store<IAppState>,
		private translate: TranslateService,
		private formBuilder: FormBuilder,
	) {
		this.postSeenByInfo = this.postSeenByInfo.bind(this);
		this.postLikedByInfo = this.postLikedByInfo.bind(this);
		this.deletePost = this.deletePost.bind(this);
		this.editPost = this.editPost.bind(this);
		this.deleteComment = this.deleteComment.bind(this);
		this.editComment = this.editComment.bind(this);
	}

	public ngOnInit(): void {
		this.initForm();
		this.initSubscriptions();
	}

	public postSeenByInfo(messageBoardPost: BulletinBoardPost): void {
		this.store.dispatch(new GetPostSeenByInfo(messageBoardPost));
		this.dialog.open(BulletinBoardPostSeenByInfoComponent, {
			panelClass: 'fullscreen-dialog-max-s',
		});
	}

	public postLikedByInfo([messageBoardPost]: [BulletinBoardPost]): void {
		if (messageBoardPost.message_board_post_likes_count === 0) {
			return;
		}
		this.dialog.open(BulletinBoardPostLikesInfoComponent, {
			data: {
				post: messageBoardPost,
				punchClock: false,
				likes: messageBoardPost.message_board_post_likes_count,
			},
			panelClass: 'fullscreen-dialog-max-s',
		});
	}

	public createComment(bulletinBoardPost: BulletinBoardPost): void {
		this.store.dispatch(new CreateBulletinBoardComment(bulletinBoardPost, this.form.value.comment));
		this.form.reset();
	}

	public editPost(bulletinBoardPost: BulletinBoardPost): void {
		this.store.dispatch(new SetSelectedPostForComments(undefined));
		this.store.dispatch(new GetBulletinPost(bulletinBoardPost.id));
	}

	public async deletePost(bulletinBoardPost: BulletinBoardPost): Promise<void> {
		const response: any = await this.dialog.open(ConfirmPopupComponent, {
			data: {
				title: this.translate.instant('BULLETINBOARD.POST_CONFIRM_DELETE'),
				rightButtonType: EQOButtonType.BIG_RED,
				leftButtonText: this.translate.instant('cancel_all'),
				rightButtonText: this.translate.instant('GLOBAL.DELETE'),
			},

		}).afterClosed().toPromise();
		if (!response || response.cancelled) {
			return;
		}
		this.store.dispatch(new DeleteBulletinPost(bulletinBoardPost.id));
	}

	public async editComment([bulletinPost, comment]): Promise<void> {
		const response = await this.dialog.open(ConfirmPopupComponent, {
			data: {
				description: this.translate.instant('BULLETINBOARD.EDIT_COMMENT'),
				inputType: EQOConfirmPopupInputType.TEXTAREA,
				inputText: comment.comment,
				rightButtonType: EQOButtonType.BIG_RED,
				leftButtonText: this.translate.instant('cancel_all'),
				rightButtonText: this.translate.instant('GLOBAL.SAVE'),
			},

		}).afterClosed().toPromise();
		if (!response || response.cancelled) {
			return;
		}
		this.store.dispatch(new PatchBulletinBoardComment(bulletinPost.id, comment.id, response.value));
	}

	public async deleteComment([bulletinPost, comment]): Promise<void> {
		const response = await this.dialog.open(ConfirmPopupComponent, {
			data: {
				title: this.translate.instant('BULLETINBOARD.COMMENT_CONFIRM_DELETE'),
				rightButtonType: EQOButtonType.BIG_RED,
				leftButtonText: this.translate.instant('cancel_all'),
				rightButtonText: this.translate.instant('GLOBAL.DELETE'),
			},
		}).afterClosed().toPromise();
		if (!response || response.cancelled) {
			return;
		}
		this.store.dispatch(new DeleteBulletinBoardComment(bulletinPost.id, comment.id));

	}

	public getMoreComments(bulletinBoardPost: BulletinBoardPost): void {
		if (this._selectedPostId === bulletinBoardPost.id) {
			this.store.dispatch(new GetBulletinPostComments(10, this.commentsPagination.current_page + 1, bulletinBoardPost.id));
		} else {
			this.store.dispatch(new SetSelectedPostForComments(bulletinBoardPost.id));
			this.store.dispatch(new GetBulletinPostComments(10, 1, bulletinBoardPost.id));
		}
	}

	public likeOrDislikePost(post: BulletinBoardPost): void {
		if (post.liked) {
			this.store.dispatch(new DislikePost(post));
		} else {
			this.store.dispatch(new LikePost(post));
		}
	}

	public trackByGroup(index: any, value: IBulletinPostGroup): any {
		return value.text;
	}

	public ngOnDestroy(): void {
		this.store.dispatch(new SetSelectedPostForComments(undefined));
		this._destroy$.next();
		this._destroy$.complete();
	}

	private initForm(): void {
		this.form = this.formBuilder.group({
			comment: [null, Validators.required],
			action: [null],
		});
	}

	private initSubscriptions(): void {
		this.commentsPagination$
			.pipe(
				takeUntil(this._destroy$),
				filter(pagination => !!pagination),
			)
			.subscribe((pagination) => this.commentsPagination = pagination);
		this.commentsPostIdSelected$
			.pipe(takeUntil(this._destroy$))
			.subscribe(postId => this._selectedPostId = postId);
	}
}
